import { useEffect, useRef, useState } from "react";
import { createPortal, flushSync } from "react-dom";
import { useLocation } from "react-router-dom";
import { useLenis } from "./SmoothScroll";
import "./PayrollPreview.css";

// Both products use the same framing and native dialog, with reviewed public assets only.
export default function ProductPreview({ product, root, heading, introduction, screens }) {
  const viewer = useRef(null);
  const closeButton = useRef(null);
  const trigger = useRef(null);
  const [selected, setSelected] = useState(null);
  const section = useRef(null);
  const { hash } = useLocation();
  const lenis = useLenis();

  useEffect(() => {
    if (hash !== "#development-preview") return undefined;
    let cancelled = false;
    // The SPA mounts after the preloader, too late for the browser's initial
    // fragment jump. Wait for fonts so the preceding sections have settled.
    document.fonts.ready.then(() => requestAnimationFrame(() => {
      if (cancelled || !section.current) return;
      const offset = (document.querySelector('[data-testid="site-header"]')?.offsetHeight || 80) + 16;
      if (lenis) lenis.scrollTo(section.current, { offset: -offset, immediate: true, force: true });
      else window.scrollTo({ top: section.current.getBoundingClientRect().top + window.scrollY - offset, behavior: "instant" });
    }));
    return () => { cancelled = true; };
  }, [hash, lenis]);

  function openImage(event, screen) {
    // Preserve native open-in-new-tab/download behavior and the PNG fallback.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || !viewer.current?.showModal) return;
    event.preventDefault();
    trigger.current = event.currentTarget;
    // Render the selected capture before exposing the native modal.
    flushSync(() => setSelected(screen));
    viewer.current.showModal();
    closeButton.current.focus({ preventScroll: true });
  }

  function cycleFocus(event) {
    if (event.key !== "Tab") return;
    // Native modal inertness protects the page; explicit cycling also keeps
    // sequential focus from leaving the viewer for browser chrome.
    const controls = [...viewer.current.querySelectorAll("a[href], button, [tabindex]")].filter((element) =>
      element.tabIndex >= 0 && !element.matches(":disabled, [aria-disabled='true']") &&
      !element.closest("[hidden], [inert]") && element.getClientRects().length > 0 &&
      getComputedStyle(element).visibility === "visible"
    );
    event.preventDefault();
    const current = controls.indexOf(document.activeElement);
    const next = current < 0 ? (event.shiftKey ? controls.length - 1 : 0)
      : (current + (event.shiftKey ? -1 : 1) + controls.length) % controls.length;
    (controls[next] || viewer.current).focus();
  }

  function closeImage() {
    // A native close event may arrive after a rapid subsequent opening.
    if (viewer.current.open) return;
    setSelected(null);
    if (trigger.current?.isConnected) trigger.current.focus({ preventScroll: true });
    trigger.current = null;
  }

  function dismissImage() {
    viewer.current.close();
    closeImage();
  }

  return (
    <>
    <section ref={section} id="development-preview" aria-labelledby={`${product}-preview-title`} className="pb-20 md:pb-32 px-3 md:px-6" data-testid={`${product}-preview`}>
      <div className="product-preview-surface max-w-7xl mx-auto px-6 md:px-12" style={{ "--product-preview-background": 'url("/images/product-preview-charcoal.webp")' }}>
        <p className="font-mono text-xs tracking-[0.2em] uppercase text-cyan-accent">Inside the development build</p>
        <h2 id={`${product}-preview-title`} className="mt-4 font-heading font-bold tracking-normal text-3xl md:text-5xl text-white max-w-3xl">
          {heading}
        </h2>
        <p className="mt-5 max-w-3xl text-zinc-400 leading-relaxed">
          {introduction}
        </p>
        <div className="mt-12 space-y-14 md:space-y-20">
          {screens.map(({ name, title, description, alt, width, height, screenLabel, linkLabel, note, qualifications, caption = "Development preview · Sample data.", fullOnly }, index) => (
            <figure key={name} data-testid={`${product}-preview-${name}`}>
              <div className="mb-5 grid gap-3 md:grid-cols-3 md:gap-8">
                <h3 className="font-heading font-bold text-xl md:text-2xl text-white">{title}</h3>
                <p className="md:col-span-2 text-sm text-zinc-400 leading-relaxed">{description}</p>
              </div>
              <div className="payroll-preview-stage">
                <div className="payroll-preview-screen">
                  <div className="payroll-preview-screenbar font-mono" aria-hidden="true">
                    {screenLabel}
                  </div>
                <picture>
                  <source type="image/webp" srcSet={fullOnly ? `${root}/${name}-public-full.webp ${width}w` : `${root}/${name}-public-half.webp ${width / 2}w, ${root}/${name}-public-full.webp ${width}w`} sizes="(min-width: 1280px) 1130px, (min-width: 768px) calc(100vw - 198px), calc(100vw - 90px)" />
                  <img src={`${root}/${name}-public.png`} alt={alt} width={width} height={height} loading="lazy" decoding="async" className={`block w-full h-auto payroll-preview-image payroll-preview-image-${name}`} />
                </picture>
                </div>
              </div>
              <figcaption className="mt-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-2 text-sm text-zinc-400">
                <span>{caption}</span>
                <a href={`${root}/${name}-public.png`} aria-haspopup="dialog" onClick={(event) => openImage(event, screens[index])} className="inline-flex min-h-[44px] items-center rounded-sm text-zinc-300 underline underline-offset-4 decoration-white/30 hover:text-cyan-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-cyan-accent">
                  {linkLabel}
                </a>
              </figcaption>
              {note && <p className="mt-2 text-xs text-zinc-400 leading-relaxed">{note}</p>}
              {qualifications && <div className="mt-5 text-sm text-zinc-300 leading-relaxed" data-preview-qualifications>
                <h4 className="font-medium text-white">About the displayed fields</h4>
                <ul className="mt-2 space-y-2 list-disc pl-5">{qualifications.map(text => <li key={text}>{text}</li>)}</ul>
              </div>}
            </figure>
          ))}
        </div>
      </div>
    </section>
    {createPortal(
      <dialog ref={viewer} className="payroll-preview-viewer" aria-labelledby={`${product}-viewer-title`} onKeyDown={cycleFocus} onCancel={(event) => { event.preventDefault(); dismissImage(); }} onClose={closeImage} data-lenis-prevent>
        <div className="payroll-preview-viewer-head">
          <h2 id={`${product}-viewer-title`} className="font-heading font-bold text-lg text-white">{selected?.viewerTitle}</h2>
          <div className="payroll-preview-viewer-actions">
            <a href={selected ? `${root}/${selected.name}-public.png` : undefined} target="_blank" rel="noopener noreferrer">Open public crop</a>
            <button ref={closeButton} type="button" onClick={dismissImage}>Close</button>
          </div>
        </div>
        <p className="px-4 pb-3 text-sm text-zinc-400">{selected?.caption || "Development preview · Sample data."} {selected?.viewerNote}</p>
        {selected?.qualifications && <ul className="px-8 pb-3 text-sm text-zinc-300 list-disc space-y-1">{selected.qualifications.map(text => <li key={text}>{text}</li>)}</ul>}
        <div className="payroll-preview-viewer-body" tabIndex={0} role="region" aria-label="Public screenshot crop; scroll to explore">
          {selected && <img src={`${root}/${selected.name}-public.png`} alt={selected.alt} width={selected.width} height={selected.height} />}
        </div>
      </dialog>, document.body
    )}
    </>
  );
}
